import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, ArrowRight, CheckCircle2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const DEADLINE = new Date("2026-10-31T23:59:59+01:00");

const remaining = () => {
  const diff = DEADLINE.getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
  };
};

export const AutumnSaleBanner = () => {
  const { lang } = useT();
  const [left, setLeft] = useState(remaining);

  useEffect(() => {
    const iv = setInterval(() => setLeft(remaining()), 30000);
    return () => clearInterval(iv);
  }, []);

  if (!left) return null;

  const de = lang === "de";
  const units = de
    ? [["Tage", left.days], ["Std", left.hours], ["Min", left.minutes]]
    : [["days", left.days], ["hrs", left.hours], ["min", left.minutes]];
  const perks = de
    ? ["Du behältst 80% deiner Gewinne statt 60%", "Alle Kunden profitieren automatisch", "Kein Abo · keine Startgebühr — Fee nur auf Gewinne"]
    : ["You keep 80% of your profits instead of 60%", "All clients benefit automatically", "No subscription · no setup fee — fee only on profits"];

  return (
    <section
      data-testid="autumn-sale-banner"
      className="relative overflow-hidden py-12 sm:py-16"
      style={{ background: "linear-gradient(135deg, #fdf3e7 0%, #fbead6 40%, #f7e3d2 100%)" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(closest-side, rgba(234,88,12,0.45), transparent 70%)" }}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 items-center gap-8 lg:gap-12">
        {/* Copy */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-white text-[11px] font-bold uppercase tracking-widest shadow-md"
            style={{ background: "linear-gradient(135deg, #ea580c 0%, #b45309 100%)" }}>
            <Leaf className="h-3.5 w-3.5" />
            {de ? "Herbst-Sale · nur bis 31. Oktober" : "Autumn Sale · until October 31"}
          </div>
          <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-slate-900">
            {de ? (
              <>Nur <span className="text-slate-400 line-through decoration-[3px] decoration-rose-400/70">40%</span>{" "}
                <span style={{ color: "#c2410c" }}>20%</span> Performance-Fee.</>
            ) : (
              <>Only <span className="text-slate-400 line-through decoration-[3px] decoration-rose-400/70">40%</span>{" "}
                <span style={{ color: "#c2410c" }}>20%</span> performance fee.</>
            )}
          </h2>
          <ul className="mt-5 space-y-2">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#c2410c" }} />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-center gap-2.5 flex-wrap" data-testid="autumn-sale-countdown">
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500 mr-1">{de ? "Endet in" : "Ends in"}</span>
            {units.map(([label, value]) => (
              <div key={label} className="text-center px-3.5 py-2 rounded-xl bg-white/90 border border-orange-200 shadow-sm min-w-[62px]">
                <div className="font-mono-tab font-bold text-2xl text-slate-900 leading-none">{value}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-1">{label}</div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex items-center gap-4 flex-wrap">
            <Link
              to="/register"
              data-testid="autumn-sale-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm sm:text-base font-display font-bold shadow-lg transition-transform hover:scale-[1.03]"
              style={{ background: "linear-gradient(135deg, #ea580c 0%, #b45309 100%)", boxShadow: "0 8px 24px rgba(234,88,12,0.35)" }}
            >
              {de ? "Jetzt Herbst-Konditionen sichern" : "Lock in autumn terms now"} <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#konditionen" data-testid="autumn-sale-secondary" className="text-sm font-semibold underline underline-offset-4" style={{ color: "#9a3412" }}>
              {de ? "Alle Konditionen" : "All conditions"}
            </a>
          </div>
        </div>

        {/* Visual */}
        <div className="relative" data-testid="autumn-sale-visual">
          <div className="absolute inset-0 rounded-[24px] blur-2xl opacity-30" style={{ background: "linear-gradient(135deg, #ea580c, #b45309)" }} />
          <img
            src="/herbst-sale.jpg"
            alt={de ? "Alphalane Herbst-Sale — nur 20% Performance-Fee bis 31. Oktober 2026" : "Alphalane Autumn Sale — only 20% performance fee until October 31, 2026"}
            className="relative w-full h-auto rounded-[24px] shadow-2xl border border-white/60"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};
